import { WpFieldConfig } from "@hooks/use-config/types";
import {
  isFieldDate,
  isFieldDateTime,
} from "@hooks/use-config/utils";
import type { DynamicFilterState, FilterStateValue } from "./types";

const SESSION_PREFIX = "report_filter_";

function sessionKey(reportId: string, field: string): string {
  return `${SESSION_PREFIX}${reportId}_${field}`;
}

export function writeFilterToSession(
  reportId: string,
  columnName: string,
  value: FilterStateValue
): void {
  try {
    if (value !== null && typeof value === "object" && "start" in value) {
      sessionStorage.setItem(sessionKey(reportId, `${columnName}_start`), JSON.stringify((value as { start: unknown }).start));
      sessionStorage.setItem(sessionKey(reportId, `${columnName}_end`), JSON.stringify((value as { end: unknown }).end));
    } else {
      sessionStorage.setItem(sessionKey(reportId, columnName), JSON.stringify(value));
    }
  } catch {
    // Silently fail
  }
}

export function clearReportFiltersFromSession(reportId: string): void {
  try {
    const prefix = SESSION_PREFIX + reportId + "_";
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(prefix))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // Silently fail
  }
}

export function isDateRangeFilter(fieldConfig: WpFieldConfig | undefined): boolean {
  if (!fieldConfig) return false;
  return isFieldDate(fieldConfig) || isFieldDateTime(fieldConfig);
}

export function initializeFilterState(
  enabledFields: string[],
  configMap: Record<string, WpFieldConfig | undefined>,
  reportId?: string
): DynamicFilterState {
  const state: DynamicFilterState = {};

  enabledFields.forEach((columnName) => {
    const fieldConfig = configMap[columnName];
    if (isDateRangeFilter(fieldConfig)) {
      let start = null;
      let end = null;
      if (reportId) {
        try {
          const s = sessionStorage.getItem(sessionKey(reportId, `${columnName}_start`));
          const e = sessionStorage.getItem(sessionKey(reportId, `${columnName}_end`));
          start = s ? JSON.parse(s) : null;
          end = e ? JSON.parse(e) : null;
        } catch {
          // Fallback to null
        }
      }
      state[columnName] = { start, end };
    } else {
      let value: FilterStateValue = [];
      if (reportId) {
        try {
          const stored = sessionStorage.getItem(sessionKey(reportId, columnName));
          value = stored ? JSON.parse(stored) : [];
        } catch {
          // Fallback to []
        }
      }
      state[columnName] = value;
    }
  });

  return state;
}
